import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useUrlSkillChatExecutors } from '#pplx/frontend/apps/ask/skills/hooks/useUrlSkillChatExecutors.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useCallback } from 'react';
const LazySkillDetailModal = Dynamic(async () => {
    const { SkillDetailModal } = await import('#pplx/frontend/apps/ask/skills/components/SkillDetailModal.js');
    return { default: SkillDetailModal };
});
const LazyClosedSourceSkillModal = Dynamic(async () => {
    const { ClosedSourceSkillModal } = await import('#pplx/frontend/apps/ask/skills/components/ClosedSourceSkillModal.js');
    return { default: ClosedSourceSkillModal };
});
const LazySkillModalDispatcher = Dynamic(async () => {
    const { SkillModalDispatcher } = await import('#pplx/frontend/apps/ask/skills/components/SkillModalDispatcher.js');
    return { default: SkillModalDispatcher };
});
/**
 * Opens whichever skill modal matches the skill: a metadata card for closed-source skills, the
 * full browse/edit modal otherwise.
 *
 * Callers holding the skill (`initialSkill`) branch immediately and only load the chunk they
 * need. Callers with just an id — deep links, thread mentions — get a dispatcher that resolves
 * the flag first, so the modal never opens in one shape and reshapes itself.
 *
 * Skill chat actions navigate to a new thread by default. Surfaces that fulfill them differently
 * pass their own executors — spaces append to the ambient ask input instead of navigating.
 */
export function useSkillModal({ executeTryInChat: executeTryInChatOverride, executeEditInChat: executeEditInChatOverride, 
// Surfaces under an AetherProvider with renderModals={false} must open through the
// app-level renderer, or nothing consumes the state they set.
openModal: openModalOverride, openConnectorModal, }) {
    const { openModal: localOpenModal } = useModalContext();
    const openModal = openModalOverride ?? localOpenModal;
    const chatExecutors = useUrlSkillChatExecutors();
    const executeTryInChat = executeTryInChatOverride ?? chatExecutors.executeTryInChat;
    const executeEditInChat = executeEditInChatOverride ?? chatExecutors.executeEditInChat;
    const openSkillModal = useCallback((params, options) => {
        const openConnectorModalFromSkill = (connectorParams) => {
            openConnectorModal({ ...connectorParams, openSkillModal });
        };
        const props = {
            ...params,
            executeTryInChat,
            executeEditInChat,
            openConnectorModal: openConnectorModalFromSkill,
        };
        const closedSource = params.initialSkill?.closed_source;
        if (closedSource === undefined) {
            return openModal(LazySkillModalDispatcher, props, options);
        }
        return openModal(closedSource ? LazyClosedSourceSkillModal : LazySkillDetailModal, props, options);
    }, [openModal, executeTryInChat, executeEditInChat, openConnectorModal]);
    return openSkillModal;
}
//# sourceMappingURL=useSkillModal.js.map